const viewErrorMessages = {
  offline_title: 'The BoxCaster Is Offline',
  offline_subtitle: ' ',
  busy_title: 'BoxCaster Is Busy Streaming Another Broadcast',
  busy_subtitle: 'Once the other is finished, this broadcast will begin.',
  no_video_input_title: 'BoxCaster Cannot Detect an Audio/Video Signal',
  no_video_input_subtitle: ' ',
  updating_firmware_title: 'BoxCaster Is Busy Updating Firmware',
  updating_firmware_subtitle: 'Once finished, this broadcast will begin.',
};

const progressDescription = {
  rtmp: {
    1: 'Waiting to create a stream.',
    5: 'Waiting to allocate a server.',
    20: 'Preparing for your broadcast.',
    60: 'Stream settings configured.',
    90: ' ',
  },
  boxcaster: {
    1: 'Waiting to allocate stream.',
    5: { title: 'Waiting for Your BoxCaster' },
    10: 'Preparing for your broadcast.',
    20: 'Testing your bandwidth.',
    40: 'Configuring your stream settings.',
    60: 'Stream settings configured.',
    90: ' ',
  },
  app: {
    1: 'Waiting to allocate stream.',
    5: { title: 'Open the Broadcaster App to Start Streaming' },
    10: 'Preparing for your broadcast.',
    20: 'Testing your bandwidth.',
    40: 'Configuring your stream settings.',
    60: 'Stream settings configured.',
    90: ' ',
  },
  recording: {
    1: 'Waiting to create a stream.',
    5: 'Waiting to allocate a server.',
    20: 'Preparing for your broadcast.',
    90: ' ',
  },
  misc: {
    busy_another: { title: 'BoxCaster is Busy Streaming Another Broadcast' },
    simultaneous: { title: 'Waiting on Simultaneous Broadcast' },
  },
};
progressDescription.switcher = progressDescription.rtmp;

export function ComputeBroadcastStatus(
  broadcast,
  view,
  boxcaster,
  streamDiagnostics,
  staticRTMPDiagnostics
) {
  if (!view) {
    // wait for player to have _something_ to show
    return null;
  } else if (broadcast.timeframe != 'preroll' && broadcast.timeframe != 'current') {
    // only care during preroll+live
    return null;
  }

  const { status, reason, progress } = view;
  const { stream_source, timeframe } = broadcast;

  let progressMsg = (progressDescription[stream_source] || {})[progress];
  let activeAlerts = _.pluck(
    _.where((streamDiagnostics || {}).alerts || [], { is_active: true }),
    'message'
  );

  let streamSourceName = 'BoxCaster';
  if (stream_source == 'rtmp') {
    streamSourceName = 'RTMP Encoder';
  } else if (stream_source == 'app') {
    streamSourceName = 'Broadcaster App';
  } else if (stream_source == 'switcher') {
    streamSourceName = 'Switcher Studio';
  } else if (stream_source == 'recording') {
    streamSourceName = 'Re-Broadcast (Simulated Live)';
  }

  // Special case of "busy broadcasting a different stream", which can't be
  // inferred from the broadcast/view directly.  Instead we assume if it's stuck at 1%
  // but the BoxCaster is reporting OK, it's caught in that scenario.
  if (boxcaster && boxcaster.status == 'broadcasting' && progress == 1) {
    progressMsg = progressDescription.misc.busy_another;
  }
  if (broadcast.is_limited_by_active_stream_count) {
    return angular.extend({ status: 'error' }, progressDescription.misc.simultaneous);
  }

  // If the view has a specific reason or status that we've defined, those take
  // precedence over everything else, as they're very specific.
  if (viewErrorMessages[`${reason}_title`]) {
    return {
      status: 'error',
      title: viewErrorMessages[`${reason}_title`],
      subtitle: viewErrorMessages[`${reason}_subtitle`],
    };
  } else if (viewErrorMessages[`${status}_title`]) {
    return {
      status: 'error',
      title: viewErrorMessages[`${status}_title`],
      subtitle: viewErrorMessages[`${status}_subtitle`],
    };
  }

  // Take into account the static RTMP diagnostics when using RTMP.
  if (stream_source == 'rtmp' || stream_source == 'switcher') {
    if (!staticRTMPDiagnostics || !staticRTMPDiagnostics.connected) {
      return {
        status: 'error',
        title: 'The RTMP Source Is Not Connected',
      };
    }
  }

  // If we're supposed to be live and the view says we're live, let's
  // dig a little deeper into diagnostics.
  if (timeframe == 'current' && status == 'live') {
    if (
      activeAlerts.indexOf('Streaming interrupted') >= 0 ||
      activeAlerts.indexOf('Waiting to receive data') >= 0
    ) {
      return {
        status: 'error',
        title: `Connection from ${streamSourceName} Was Lost`,
        subtitle: '',
      };
    } else if (streamDiagnostics && streamDiagnostics.status == 'Critical') {
      return {
        status: 'error',
        title: 'Live (Broadcast Health Warning)',
        subtitle: activeAlerts.join(', '),
      };
    } else if (streamDiagnostics && streamDiagnostics.status == 'Warning') {
      return {
        status: 'warning',
        title: 'Live',
        subtitle: activeAlerts.join(', '),
      };
    } else {
      return {
        status: 'ok',
        title: 'Live',
        subtitle: '',
      };
    }
  }

  // Pre-roll states need to look at diagnostics and progress %
  if (timeframe == 'preroll') {
    if (streamDiagnostics && streamDiagnostics.status == 'OK') {
      return {
        status: 'ok',
        title: 'Ready To Go Live',
        subtitle: '',
      };
    } else {
      if (progressMsg && progressMsg.title) {
        return {
          status: 'warning',
          title: progressMsg.title,
          subtitle: progressMsg.subtitle || '',
        };
      } else {
        return {
          status: 'warning',
          title: 'Getting Ready',
          subtitle: progressMsg || `Catching up at ${progress}%`,
        };
      }
    }
  }

  // Finally, if we're supposed to be live but aren't yet, let's look
  // at how much we know based on diagnostics/progress
  if ((streamDiagnostics || {}).status == 'OK' && progress == 90) {
    return {
      status: 'ok',
      title: 'Live',
      subtitle: 'Preparing the stream for viewers.',
    };
  } else if (progressMsg && progressMsg.title) {
    return {
      status: 'error',
      title: progressMsg.title,
      subtitle: progressMsg.subtitle || '',
    };
  } else {
    return {
      status: 'error',
      title: 'The Stream Has Not Yet Started',
      subtitle: progressMsg || `Catching up at ${progress}%`,
    };
  }
}

export function ComputePastBroadcastStarRating(streamDiagnostics) {
  var status = streamDiagnostics.summary_status;
  if (!status) {
    return 0;
  }
  var hasCritical = _.find(streamDiagnostics.alerts, { level: 'Critical' });
  var hasWarning = _.find(streamDiagnostics.alerts, { level: 'Warning' });
  if (status == 'OK' && (streamDiagnostics.alerts || []).length == 0) {
    return 5;
  }
  if (status == 'OK' && !hasCritical) {
    return 4.5;
  }
  if (status == 'OK') {
    return 4;
  }
  if (status == 'Warning') {
    return 3;
  }
  const avgTotalBitrate = streamDiagnostics.average_total_bitrate || streamDiagnostics.avg_bitrate;
  if (status == 'Critical' && avgTotalBitrate > 300) {
    return 2;
  }
  if (status == 'Critical' && avgTotalBitrate > 10) {
    return 1;
  }
  if (status == 'Critical' && avgTotalBitrate == 0) {
    return 0;
  }
}

export function ComputePastBroadcastRecommendations(broadcast, streamDiagnostics) {
  var result = [];
  if (!streamDiagnostics.summary_status) {
    return result;
  }
  if (streamDiagnostics.packet_loss_per_minute > 5) {
    result.push({
      text: `Packet loss detected. This can be due to an unstable internet connection.`,
      link: 'https://support.boxcast.com/en/articles/4235089',
      linkText: 'See tips for reducing packet loss.',
    });
  }
  if (findAlert(streamDiagnostics, 'Transcode delayed')) {
    result.push({
      text: `Transcode delay detected. Ensure proper RTMP encoder settings to avoid viewer buffering.`,
      link: 'https://support.boxcast.com/en/articles/4235181',
      linkText: 'Learn how to configure your RTMP encoder.',
    });
  }
  if (findAlert(streamDiagnostics, 'Ingest latency too high')) {
    result.push({
      text: `High ingest latency detected; your encoder or network can't keep up.`,
      link: 'https://support.boxcast.com/en/articles/4235181',
      linkText: 'Learn how to configure your RTMP encoder.',
    });
  }
  if (findAlert(streamDiagnostics, 'Ingest latency too low')) {
    result.push({
      text: `Negative ingest latency detected; your encoder is sending data too fast.`,
      link: 'https://support.boxcast.com/en/articles/4235181',
      linkText: 'Learn how to configure your RTMP encoder.',
    });
  }
  if (findAlert(streamDiagnostics, 'Streaming interrupted')) {
    var x = findAlert(streamDiagnostics, 'Streaming interrupted');
    // check if it only appears at the end of the broadcast; sign that you unplugged
    // early (probably intentionally), so different than losing internet mid-stream
    if (x.is_active || x.inactive_at >= broadcast.stops_at) {
      result.push({
        text: `Stream interruption detected at end of broadcast. Use the stop broadcast button on the dashboard before disconnecting your encoder to avoid viewer buffering.`,
        link: '',
        linkText: '',
      });
    } else {
      result.push({
        text: `Stream interruption detected. This can be due to an unstable internet connection.`,
        link: 'https://www.boxcast.com/blog/what-upload-speed-do-i-need-to-stream',
        linkText: 'Read about recommended upload speeds.',
      });
    }
  }
  return result;
}

function findAlert(streamDiagnostics, t) {
  var alerts = streamDiagnostics.alerts || [];
  for (var i = 0; i < alerts.length; i++) {
    if (alerts[i].message.toUpperCase().indexOf(t.toUpperCase()) >= 0) {
      return alerts[i];
    }
  }
  return null;
}

export function GetBitrateLimitsForBroadcast(apiConfig, broadcast) {
  const ret = {};

  // Pick off limits based on the stream source
  switch (broadcast.stream_source) {
    case 'boxcaster':
      switch ((broadcast.boxcaster_id || '')[0]) {
        // HDSD2 and friends
        case 'l': // fall-through
        case 'n': // fall-through
        case 'o':
          ret.video = ((apiConfig.boxcaster || {}).video_bitrate_limits || {}).hdsd2 || {};
          ret.audio = ((apiConfig.boxcaster || {}).audio_bitrate_limits || {}).hdsd2 || {};
          break;

        // BoxCaster Pro
        case 'p':
          ret.video = ((apiConfig.boxcaster || {}).video_bitrate_limits || {}).pro || {};
          ret.audio = ((apiConfig.boxcaster || {}).audio_bitrate_limits || {}).pro || {};
          break;

        default:
          return ret;
      }
      break;

    // Broadcaster
    case 'app':
      ret.video = ((apiConfig.boxcaster || {}).video_bitrate_limits || {}).broadcaster || {};
      ret.audio = ((apiConfig.boxcaster || {}).audio_bitrate_limits || {}).broadcaster || {};
      break;

    // RTMP and Switcher
    case 'rtmp': // fall-through
    case 'switcher':
      ret.video = ((apiConfig.boxcaster || {}).video_bitrate_limits || {}).rtmp || {};
      ret.audio = ((apiConfig.boxcaster || {}).audio_bitrate_limits || {}).rtmp || {};
      break;

    default:
      return ret;
  }

  // Now narrow down video based on transcoder profile
  const transcoderProfile = broadcast.transcoder_profile || '';
  if (transcoderProfile == 'audio') {
    delete ret.video;
  } else {
    const resolutionKey = parseInt(transcoderProfile).toString();
    const frameRateKey = transcoderProfile.match(/p60/) ? 'high_frame_rate' : 'low_frame_rate';
    const videoCodecKey = transcoderProfile.match(/:hevc/) ? 'hevc' : 'avc';
    ret.video = ((ret.video[resolutionKey] || {})[frameRateKey] || {})[videoCodecKey] || {};
  }

  return ret;
}

// V3Timestamps plucks timestamps off of a data version 3 stream diagnostics result
export function V3Timestamps(s, ...args) {
  while (args.length > 0) {
    s = s[args.shift()] || {};
  }
  return _.pluck(s, 0);
}

// V3Values plucks values off of a data version 3 stream diagnostics result
export function V3Values(s, ...args) {
  while (args.length > 0) {
    s = s[args.shift()] || {};
  }
  return _.pluck(s, 1);
}

// V3PlaceholderTimestamps fills a data series (data values in `datas`, timestamps in `timestamps`) with a placeholder
// (currently `null`) for any missing timestamps. The full set of timestamps is passed in `fullTimestamps`.
export function V3PlaceholderTimestamps(data, timestamps, fullTimestamps) {
  const newData = [];
  const PLACEHOLDER = null;

  // data and timestamps should have the same length; if they don't, yell loudly and
  // use the smaller value
  let dataLen = data.length;
  if (timestamps.length != dataLen) {
    console.error(
      `placeholderTimestamps: data.length (${dataLen}) does not match timestamps.length (${timestamps.length})!`
    );
    if (timestamps.length < dataLen) {
      dataLen = timestamps.length;
    }
  }

  let idx = 0;
  let fullIdx = 0;
  while (true) {
    if (fullIdx >= fullTimestamps.length) break;
    const nextData = data[idx];
    const nextTimestamp = timestamps[idx];
    const nextFullTimestamp = fullTimestamps[fullIdx];
    if (nextTimestamp == nextFullTimestamp) {
      // Timestamps match; emit data and advance both indices
      newData.push(nextData);
      idx++;
      fullIdx++;
    } else if (!nextTimestamp || nextFullTimestamp < nextTimestamp) {
      // Emit the placeholder and advance the full index
      newData.push(PLACEHOLDER);
      fullIdx++;
    } else {
      // This is unexpected, as all the timestamps should be included in fullTimestamps. Yell loudly,
      // omit this data from newData, and advance idx.
      console.error(
        `placeholderTimestamps: timestamp ${nextTimestamp} was present in timestamps, but not in fullTimestamps!`
      );
      idx++;
    }
  }

  return newData;
}

export function HasIntegratedScoreboard(scoreboardConfiguration) {
  if (scoreboardConfiguration && scoreboardConfiguration.scoreboard_make) {
    const m = scoreboardConfiguration.scoreboard_make;
    if (m == 'BoxCast' || m == 'Daktronics' || m == 'Trans-Lux Fair-Play' || m == 'OES') {
      return true;
    }
  }
  return false;
}

function hasAnyNonZeroData(data) {
  return _.some(data, (d) => d != 0);
}

export function V3HasAnyScoreboardData(streamDiagnostics) {
  if (!streamDiagnostics) return false;

  const scoreboardInformation = streamDiagnostics.scoreboard;
  if (scoreboardInformation) {
    if (!!scoreboardInformation.events && scoreboardInformation.events.length) return true;
    const scoreboardAssist = scoreboardInformation.scoreboard_assist;
    if (scoreboardAssist) {
      if (!!scoreboardAssist.events && scoreboardAssist.events.length) return true;
      if (scoreboardAssist.operation_mode) return true;
      const identity = scoreboardAssist.identity;
      if (!!identity && (!!identity.box || identity.scoreboard)) return true;
    }
  }

  const stats = streamDiagnostics.raw_stats || streamDiagnostics.raw_stats_coarse;
  if (stats) {
    const scoreBitrateData = V3Values(stats, 'bitrate', 'score');
    if (!!scoreBitrateData && hasAnyNonZeroData(scoreBitrateData)) return true;
    const scorePacketLossData = V3Values(stats, 'packet_loss', 'score');
    if (!!scorePacketLossData && hasAnyNonZeroData(scorePacketLossData)) return true;
  }

  const scoreboardStats =
    streamDiagnostics.raw_scoreboard_stats || streamDiagnostics.raw_scoreboard_stats_coarse;
  if (scoreboardStats) {
    const rssi = ((scoreboardStats || {}).scoreboard_assist || {}).rssi;
    if (rssi) {
      const boxRSSIData = V3Values(rssi, 'box');
      if (!!boxRSSIData && hasAnyNonZeroData(boxRSSIData)) return true;
      const scoreboardRSSIData = V3Values(rssi, 'scoreboard');
      if (!!scoreboardRSSIData && hasAnyNonZeroData(scoreboardRSSIData)) return true;
    }

    const linkStatus = ((scoreboardStats || {}).scoreboard_assist || {}).link_status;
    if (linkStatus) {
      const boxLinkStatusData = V3Values(linkStatus, 'box');
      if (!!boxLinkStatusData && hasAnyNonZeroData(boxLinkStatusData)) return true;
      const scoreboardLinkStatusData = V3Values(linkStatus, 'scoreboard');
      if (!!scoreboardLinkStatusData && hasAnyNonZeroData(scoreboardLinkStatusData)) return true;
    }
  }

  return false;
}

// ParseScoreboardEvents walks through scoreboard events and scoreboard assist events, formatting them into objects
// suitable for listing/display in the scoreboard events section and sorting them by timestamp.
export function ParseScoreboardEvents(streamDiagnostics, scoreboardConfiguration) {
  let scoreboardEvents = [];
  _.each(((streamDiagnostics || {}).scoreboard || {}).events || [], (e) => {
    switch (e.code) {
      case 'sport_change':
        let detectedSport = e.data.to;
        if (detectedSport.startsWith('Basketball')) {
          detectedSport = 'Basketball';
        }
        scoreboardEvents.push({
          timestamp: moment(e.timestamp).toDate(),
          message: `Detected sport ${detectedSport}`,
        });
        break;

      case 'serial_port_state_change':
        let deviceName = 'Scoreboard adapter';
        if ((scoreboardConfiguration || {}).scoreboard_make == 'BoxCast')
          deviceName = 'Scoreboard Assist';
        let whatHappened = 'connected to';
        if (!e.data.to) whatHappened = 'disconnected from';
        scoreboardEvents.push({
          timestamp: moment(e.timestamp).toDate(),
          message: `${deviceName} ${whatHappened} BoxCaster`,
        });
        break;
    }
  });
  _.each(
    (((streamDiagnostics || {}).scoreboard || {}).scoreboard_assist || {}).events || [],
    (e) => {
      const side = { box: 'BoxCaster', scoreboard: 'Scoreboard', '': '' }[
        (e.data || {}).side || ''
      ];
      switch (e.code) {
        case 'link_state':
          if (side) {
            scoreboardEvents.push({
              timestamp: moment(e.timestamp).toDate(),
              message: `${side} side link is now ${e.data.to}`,
            });
          }
          break;

        case 'interface':
          const iface = { nevco: 'Nevco', 'rs-232': 'RS-232', 'rs-422': 'RS-422', '': '' }[
            e.data.to || ''
          ];
          if (iface) {
            scoreboardEvents.push({
              timestamp: moment(e.timestamp).toDate(),
              message: `Switching to the ${iface} interface`,
            });
          }
          break;

        case 'make_model':
          const make = e.data.to.make;
          const model = e.data.to.model;
          if (make != 'none' && model != 'none') {
            let startsWithVowel = false;
            if ('aeiou'.indexOf(make.toLowerCase()[0]) != -1) startsWithVowel = true;
            scoreboardEvents.push({
              timestamp: moment(e.timestamp).toDate(),
              message: `Detected a${startsWithVowel ? 'n' : ''} ${make} ${model}`,
            });
          }
          break;

        case 'rs422_jack_inserted':
          scoreboardEvents.push({
            timestamp: moment(e.timestamp).toDate(),
            message: `RS-422 jack inserted`,
          });
          break;

        case 'rs422_jack_removed':
          scoreboardEvents.push({
            timestamp: moment(e.timestamp).toDate(),
            message: `RS-422 jack removed`,
          });
          break;

        case 'pairing_mode_entered':
          if (side) {
            scoreboardEvents.push({
              timestamp: moment(e.timestamp).toDate(),
              message: `${side} side entered pairing mode`,
            });
          }
          break;

        case 'pairing_mode_exited':
          if (side) {
            scoreboardEvents.push({
              timestamp: moment(e.timestamp).toDate(),
              message: `${side} side exited pairing mode`,
            });
          }
          break;

        case 'rs422_termination_set':
          scoreboardEvents.push({
            timestamp: moment(e.timestamp).toDate(),
            message: `RS-422 termination turned ${e.data.termination ? 'on' : 'off'}`,
          });
          break;

        // IGNORING FOR NOW:
        // ScoreboardAssistEventCodeBaudRate  = ScoreboardAssistEventCode("baud_rate")
        // ScoreboardAssistEventCodeMiscellaneousFailure = ScoreboardAssistEventCode("miscellaneous_failure")
        // ScoreboardAssistEventCodeNevcoDetected        = ScoreboardAssistEventCode("nevco_detected")
        // ScoreboardAssistEventCodeNevcoSignalLost      = ScoreboardAssistEventCode("nevco_signal_lost")
        // ScoreboardAssistEventCodeUARTDetected         = ScoreboardAssistEventCode("uart_detected")
        // ScoreboardAssistEventCodeAutobaudLocked       = ScoreboardAssistEventCode("autobaud_locked")
        // ScoreboardAssistEventCodeAutobaudFailed       = ScoreboardAssistEventCode("autobaud_failed")
      }
    }
  );
  return _.sortBy(scoreboardEvents, (e) => e.timestamp);
}

// FilterRSSIForLinkStatus filters the RSSI values to remove values when the pair was unlinked.
export function FilterRSSIForLinkStatus(scoreboardAssistStats) {
  const rssi = (scoreboardAssistStats || {}).rssi || {};
  const boxRSSI = rssi.box || [];
  const scoreboardRSSI = rssi.scoreboard || [];
  const boxLinkStatus = ((scoreboardAssistStats || {}).link_status || {}).box || [];
  if (!boxRSSI.length || !scoreboardRSSI.length || !boxLinkStatus.length) return;

  let idxBoxRSSI = 0;
  let idxScoreboardRSSI = 0;
  for (let idxBoxLinkStatus = 0; idxBoxLinkStatus < boxLinkStatus.length; idxBoxLinkStatus++) {
    // Get the link status timestamp
    const linkStatusTimestamp = boxLinkStatus[idxBoxLinkStatus][0];

    // Advance the other indices until we get to a matching timestamp
    let boxRSSITimestamp = (boxRSSI[idxBoxRSSI] || [])[0];
    while (idxBoxRSSI < boxRSSI.length && boxRSSITimestamp < linkStatusTimestamp) {
      idxBoxRSSI++;
      boxRSSITimestamp = (boxRSSI[idxBoxRSSI] || [])[0];
    }
    let scoreboardRSSITimestamp = (scoreboardRSSI[idxScoreboardRSSI] || [])[0];
    while (
      idxScoreboardRSSI < scoreboardRSSI.length &&
      scoreboardRSSITimestamp < linkStatusTimestamp
    ) {
      idxScoreboardRSSI++;
      scoreboardRSSITimestamp = (scoreboardRSSI[idxScoreboardRSSI] || [])[0];
    }

    // If the pair is not linked at this timestamp, set any values to null
    const linkValue = (boxLinkStatus[idxBoxLinkStatus] || [])[1];
    if (linkValue != 3) {
      if (idxBoxRSSI < boxRSSI.length && boxRSSITimestamp == linkStatusTimestamp) {
        boxRSSI[idxBoxRSSI][1] = null;
      }
      if (
        idxScoreboardRSSI < scoreboardRSSI.length &&
        scoreboardRSSITimestamp == linkStatusTimestamp
      ) {
        scoreboardRSSI[idxScoreboardRSSI][1] = null;
      }
    }
  }
}

// baselineStats accepts an array of fine or coarse stats and baselines the values such that all
// values are greater than or equal to zero. It also returns the minimum value.
function baselineStats(stats) {
  if (!stats || stats.length == 0) {
    return 0;
  }

  const min = Math.min(...V3Values(stats));
  stats.forEach((e) => (e[1] -= min));
  return min;
}

// maxFineStatTimeRange is the maximum broadcast duration under which the "fine" stats will be used
// for calculating ingest latency. Broadcasts longer than this will use the coarse stats.
const maxFineStatTimeRange = 60 * 60 * 1000;

// BaselineIngestLatency baselines the fine and coarse RTMP ingest latency stats such that all
// values are greater than or equal to zero. It also adjusts the last, average, and max ingest
// latency (when present) to take into account the minimum recorded value.
export function BaselineIngestLatency(streamDiagnostics, broadcast) {
  if (!streamDiagnostics) {
    return;
  }

  let fineLatencyMin = 0;
  if (streamDiagnostics.raw_stats && streamDiagnostics.raw_stats.ingest_latency) {
    fineLatencyMin = baselineStats(streamDiagnostics.raw_stats.ingest_latency);
  }

  let coarseLatencyMin = 0;
  if (streamDiagnostics.raw_stats_coarse && streamDiagnostics.raw_stats_coarse.ingest_latency) {
    coarseLatencyMin = baselineStats(streamDiagnostics.raw_stats_coarse.ingest_latency);
  }

  // last_ingest_latency should be adjusted by the fine results
  if (_.isNumber(streamDiagnostics.last_ingest_latency) && _.isNumber(fineLatencyMin)) {
    streamDiagnostics.last_ingest_latency -= fineLatencyMin;
  }

  // if broadcast duration > 1 hour, max_ingest_latency and average_ingest_latency use coarse results; otherwise they use fine results.
  const broadcastDuration = moment(broadcast.stops_at) - moment(broadcast.starts_at);
  const adjustmentLatency =
    broadcastDuration > maxFineStatTimeRange ? coarseLatencyMin : fineLatencyMin;
  if (_.isNumber(streamDiagnostics.max_ingest_latency) && _.isNumber(adjustmentLatency)) {
    streamDiagnostics.max_ingest_latency -= adjustmentLatency;
  }
  if (_.isNumber(streamDiagnostics.average_ingest_latency) && _.isNumber(adjustmentLatency)) {
    streamDiagnostics.average_ingest_latency -= adjustmentLatency;
  }

  // There was a bug in stream diagnostics service that took the max absolute value for
  // max_ingest_latency rather than the max value. So here we just sanity-check max_ingest_latency
  // in case average_ingest_latency or last_ingest_latency is higher.
  let maxFineLatency = 0;
  if (streamDiagnostics.raw_stats && streamDiagnostics.raw_stats.ingest_latency) {
    maxFineLatency = Math.max(...V3Values(streamDiagnostics.raw_stats.ingest_latency));
  }
  let maxCoarseLatency = 0;
  if (streamDiagnostics.raw_stats_coarse && streamDiagnostics.raw_stats_coarse.ingest_latency) {
    maxCoarseLatency = Math.max(...V3Values(streamDiagnostics.raw_stats_coarse.ingest_latency));
  }
  const maxLatency = Math.max(
    maxFineLatency,
    maxCoarseLatency,
    streamDiagnostics.last_ingest_latency || 0,
    streamDiagnostics.average_ingest_latency || 0
  );
  if (!streamDiagnostics.max_ingest_latency || maxLatency > streamDiagnostics.max_ingest_latency) {
    streamDiagnostics.max_ingest_latency = maxLatency;
  }
}



// WEBPACK FOOTER //
// ./app/utils/stream-diagnostics.js